// Dashboard View - Site Listing
import { getSites, createSite, deleteSite } from '../firebase/db.js';
import { getCurrentUser } from '../firebase/auth.js';
import router from '../router.js';
import { createModal, showModal, hideModal, initModals, confirm } from '../components/Modal.js';
import { toast } from '../components/Toast.js';
import { t } from '../i18n.js';

/**
 * Create dashboard view
 * @param {Array} sites - List of sites
 * @returns {string} HTML string
 */
export function createDashboardView(sites = []) {
  return `
    <div class="page">
      <div class="container">
        <div class="page-header flex justify-between items-center flex-mobile-column">
          <div>
            <h1 class="page-title">${t('dashboard.title')}</h1>
            <p class="page-subtitle">${t('login.subtitle')}</p>
          </div>
          <button class="btn btn-primary" id="create-site-btn" style="width: auto;">
            <span>+</span>
            <span>${t('dashboard.addSite')}</span>
          </button>
        </div>
        
        ${sites.length > 0 ? `
          <div class="dashboard-stats">
            <div class="stat-card card">
              <div class="stat-icon">📍</div>
              <div class="stat-content">
                <div class="stat-value">${sites.length}</div>
                <div class="stat-label">${t('dashboard.statusActive')}</div>
              </div>
            </div>
            <div class="stat-card card">
              <div class="stat-icon">🗿</div>
              <div class="stat-content">
                <div class="stat-value">${sites.reduce((acc, s) => acc + (s.assetCount || 0), 0)}</div>
                <div class="stat-label">${t('site.tabAssets')}</div>
              </div>
            </div>
            <div class="stat-card card">
              <div class="stat-icon">📓</div>
              <div class="stat-content">
                <div class="stat-value">${sites.reduce((acc, s) => acc + (s.logCount || 0), 0)}</div>
                <div class="stat-label">${t('site.tabLogbook')}</div>
              </div>
            </div>
          </div>
          
          <div class="grid grid-cols-3" id="sites-grid">
            ${sites.map(site => createSiteCard(site)).join('')}
          </div>
        ` : `
          <div class="empty-state">
            <div class="empty-state-icon">🏛️</div>
            <h2 class="empty-state-title">${t('dashboard.noSites')}</h2>
            <button class="btn btn-primary btn-lg mt-4" id="create-first-site-btn">
              ${t('dashboard.createSite')}
            </button>
          </div>
        `}
      </div>
    </div>
    
    <!-- Create Site Modal -->
    ${createModal('create-site-modal', t('dashboard.createSite'), createSiteForm(), {
    confirmText: t('dashboard.createSite'),
    size: 'large'
  })}
    
    <style>
      .dashboard-stats {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: var(--space-5);
        margin-bottom: var(--space-8);
      }
      
      .stat-card {
        display: flex;
        align-items: center;
        gap: var(--space-4);
        padding: var(--space-5);
      }
      
      .stat-icon {
        font-size: 2rem;
      }
      
      .stat-value {
        font-size: var(--text-2xl);
        font-weight: 700;
        color: var(--color-accent-primary);
      }
      
      .stat-label {
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }
      
      @media (max-width: 768px) {
        .dashboard-stats {
          grid-template-columns: 1fr;
        }
      }
    </style>
  `;
}

/**
 * Create site card HTML
 * @param {Object} site 
 * @returns {string} HTML string
 */
function createSiteCard(site) {
  const periods = {
    neolithic: t('dashboard.sitePeriod') + ' - Neolithic',
    bronze_age: t('dashboard.sitePeriod') + ' - Bronze Age',
    iron_age: t('dashboard.sitePeriod') + ' - Iron Age',
    classical: t('dashboard.sitePeriod') + ' - Classical',
    medieval: t('dashboard.sitePeriod') + ' - Medieval',
    other: t('dashboard.sitePeriod') + ' - Other'
  };

  return `
    <div class="card site-card" data-site-id="${site.id}">
      <div class="site-card-placeholder">🏛️</div>
      <h3 class="card-title">${escapeHtml(site.name)}</h3>
      <p class="card-body" style="margin-bottom: 0;">
        ${escapeHtml(site.location || '-')}
      </p>
      <div class="site-card-meta" style="display: flex; flex-wrap: wrap; gap: var(--space-2); margin-top: var(--space-3);">
        <span class="badge badge-primary">${periods[site.period] || t('dashboard.sitePeriod')}</span>
        ${site.assetCount ? `<span class="badge" style="background: rgba(255,255,255,0.05)">🗿 ${site.assetCount}</span>` : ''}
        ${site.logCount ? `<span class="badge" style="background: rgba(255,255,255,0.05)">📓 ${site.logCount}</span>` : ''}
        ${site.role === 'member'
      ? `<span class="badge badge-warning" style="background: var(--color-accent-secondary); color: white;">Member</span>`
      : ''}
      </div>
      <div class="card-footer flex justify-between items-center">
        <button class="btn btn-secondary btn-sm site-open-btn">Open</button>
        ${site.role !== 'member' ? `<button class="btn btn-ghost btn-sm btn-icon site-delete-btn" title="${t('app.delete')}">🗑️</button>` : ''}
      </div>
    </div>
  `;
}

/**
 * Create site form HTML
 * @returns {string} HTML string
 */
function createSiteForm() {
  return `
    <form id="site-form">
      <div class="form-group">
        <label class="form-label" for="site-name">Alan Adı *</label>
        <input 
          type="text" 
          id="site-name" 
          class="form-input" 
          placeholder="e.g., Göbekli Tepe Sector A"
          required
        >
      </div>
      
      <div class="form-group">
        <label class="form-label" for="site-location">Konum</label>
        <input 
          type="text" 
          id="site-location" 
          class="form-input" 
          placeholder="e.g., Şanlıurfa Province, Turkey"
        >
      </div>
      
      <div class="form-group">
        <label class="form-label" for="site-coords">Koordinatlar (opsiyonel)</label>
        <input 
          type="text" 
          id="site-coords" 
          class="form-input" 
          placeholder="e.g., 37.2233° N, 38.9222° E"
        >
      </div>
      
      <div class="form-group">
        <label class="form-label" for="site-period">Tarihi Dönem</label>
        <select id="site-period" class="form-select">
          <option value="">Bir dönem seçin</option>
          <option value="neolithic">Neolitik</option>
          <option value="bronze_age">Tunç Çağı</option>
          <option value="iron_age">Demir Çağı</option>
          <option value="classical">Klasik Antik Çağ</option>
          <option value="medieval">Orta Çağ</option>
          <option value="other">Diğer</option>
        </select>
      </div>
      
      <div class="form-group">
        <label class="form-label" for="site-supervisor">Kazı Başkanı</label>
        <input 
          type="text" 
          id="site-supervisor" 
          class="form-input" 
          placeholder="e.g., Dr. Klaus Schmidt"
        >
      </div>
      
      <div class="form-group">
        <label class="form-label" for="site-code">Kazı Kodu</label>
        <input 
          type="text" 
          id="site-code" 
          class="form-input" 
          placeholder="e.g., GT-2024-A"
        >
      </div>
      
      <div class="form-group">
        <label class="form-label" for="site-description">Açıklama</label>
        <textarea 
          id="site-description" 
          class="form-textarea" 
          placeholder="Kazı alanı hakkında kısa açıklama..."
          rows="3"
        ></textarea>
      </div>
    </form>
  `;
}

/**
 * Initialize dashboard view event listeners
 */
export function initDashboardView() {
  initModals();

  // Create site buttons
  const createBtns = ['create-site-btn', 'create-first-site-btn'];
  createBtns.forEach(id => {
    const btn = document.getElementById(id);
    if (btn) {
      btn.addEventListener('click', () => showModal('create-site-modal'));
    }
  });

  // Confirm button in modal
  const confirmBtn = document.querySelector('#create-site-modal [data-modal-confirm]');
  if (confirmBtn) {
    confirmBtn.addEventListener('click', handleCreateSite);
  }

  // Site card clicks
  document.querySelectorAll('.site-card').forEach(card => {
    const siteId = card.dataset.siteId;

    // Open button
    card.querySelector('.site-open-btn')?.addEventListener('click', (e) => {
      e.stopPropagation();
      router.navigate(`/site/${siteId}`);
    });

    // Delete button
    card.querySelector('.site-delete-btn')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      const confirmed = await confirm(
        'Alanı Sil',
        'Bu alanı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.'
      );
      if (confirmed) {
        await handleDeleteSite(siteId);
      }
    });

    // Card click - open site
    card.addEventListener('click', () => {
      router.navigate(`/site/${siteId}`);
    });
  });
}

/**
 * Handle create site form submission
 */
async function handleCreateSite() {
  const form = document.getElementById('site-form');
  const name = document.getElementById('site-name').value.trim();

  if (!name) {
    toast.error('Alan adı gereklidir');
    return;
  }

  const user = getCurrentUser();
  if (!user) {
    toast.error('Giriş yapmalısınız');
    router.navigate('/login');
    return;
  }

  const siteData = {
    name,
    location: document.getElementById('site-location').value.trim(),
    coordinates: document.getElementById('site-coords').value.trim(),
    period: document.getElementById('site-period').value,
    supervisor: document.getElementById('site-supervisor').value.trim(),
    excavationCode: document.getElementById('site-code').value.trim(),
    description: document.getElementById('site-description').value.trim(),
    userId: user.uid,
    assetCount: 0,
    logCount: 0
  };

  try {
    await createSite(siteData);
    toast.success('Alan başarıyla oluşturuldu!');
    hideModal('create-site-modal');

    // Refresh the page
    router.handleRoute();
  } catch (error) {
    console.error('Create site error:', error);
    toast.error('Alan oluşturulamadı. Lütfen tekrar deneyin.');
  }
}

/**
 * Handle delete site
 * @param {string} siteId 
 */
async function handleDeleteSite(siteId) {
  try {
    await deleteSite(siteId);
    toast.success('Alan silindi');
    router.handleRoute();
  } catch (error) {
    console.error('Delete site error:', error);
    toast.error('Alan silinemedi');
  }
}

/**
 * Escape HTML for safe rendering
 * @param {string} str 
 * @returns {string}
 */
function escapeHtml(str) {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}
